import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalService } from 'src/app/api/global.service';
import { ApiService } from 'src/app/api/api.service';
import { AuthService } from 'src/app/auth.service';
import { AlertController, ModalController } from '@ionic/angular';
import { FileDropboxPage } from 'src/app/modals/files/file-dropbox/file-dropbox.page';
import { CreatePage } from 'src/app/appointments/create/create.page';

@Component({
  selector: 'app-top-navbar',
  templateUrl: './top-navbar.component.html',
  styleUrls: ['./top-navbar.component.scss'],
})
export class TopNavbarComponent implements OnInit, OnDestroy {

  user: any = null;
  missing_info_count = 0;
  private missing_info_interval: any;
  private auth_heartbeat_interval: any;

  // Whether appointment booking is enabled for this firm, and the client's own upcoming self-booked
  // appointment (if any) - drives whether the navbar button reads "Book" vs "Manage", or hides entirely.
  // Defaults to hidden (enabled: false) so the button never flashes on screen before GetAppointmentStatus()
  // has actually confirmed it should be shown.
  appointment_status: { enabled: boolean; booking: any } = { enabled: false, booking: null };

  constructor(
    public globalService: GlobalService,
    private apiService: ApiService,
    private authService: AuthService,
    private alertController: AlertController,
    private modalController: ModalController,
    private router: Router,
  ) { }

  ngOnInit() {
    this.GetUser();
    this.GetMissingInfoCount();
    this.GetAppointmentStatus();
    this.missing_info_interval = setInterval(() => this.GetMissingInfoCount(), 5 * 60 * 1000);
    this.auth_heartbeat_interval = setInterval(() => this.CheckAuthHeartbeat(), 2 * 60 * 1000);
  }

  // Polls the server every 2 minutes to make sure the session is still valid, independent of any page
  // navigation - AuthGuard/AuthService.checkSession() only ever check once per app load and then trust
  // a cached result forever, so they'd never notice a session that died while the client just sat on a
  // page (expired cookie, logged out elsewhere, revoked server-side, etc).
  async CheckAuthHeartbeat() {
    this.authService.verifySession().subscribe((is_authenticated: boolean) => {
      if (is_authenticated) return;
      clearInterval(this.auth_heartbeat_interval);
      this.globalService.presentToastMessage('Your session has expired. Please log in again.', false);
      this.router.navigate(['/login']);
    });
  }

  async GetAppointmentStatus() {
    const data: any = await this.apiService.get('appointment/status');
    if (!data?.success) return;
    const status = Array.isArray(data.data) ? data.data[0] : data.data;
    if (!status) return;

    // Same trailing-'Z' quirk as elsewhere in this app (see GlobalService.DateToUTCFormat) - the API
    // returns these without one, which the date pipe would otherwise read as local time, not UTC.
    if (status.booking) {
      if (status.booking.start_time && !status.booking.start_time.endsWith('Z')) status.booking.start_time += 'Z';
      if (status.booking.end_time && !status.booking.end_time.endsWith('Z')) status.booking.end_time += 'Z';
    }

    this.appointment_status = status;
  }

  ngOnDestroy() {
    clearInterval(this.missing_info_interval);
    clearInterval(this.auth_heartbeat_interval);
  }

  async GetMissingInfoCount() {
    this.missing_info_count = 0;
    const data: any = await this.apiService.get('missing_information');
    if (data?.success && Array.isArray(data.data)) {
      for (const item of data.data) {
        if (!item.client_completed && !item.completed) {
          this.missing_info_count++;
        }
      }
    }
  }

  async GetUser() {
    this.user = await this.globalService.GetUser();
    console.log(this.user);
  }

  async OpenFileDropbox() {
    const modal = await this.modalController.create({
      component: FileDropboxPage,
      backdropDismiss: true,
      cssClass: 'small-modal tall-modal'
    });
    modal.present();

    await modal.onWillDismiss();
  }

  async OpenBookAppointment() {
    // Re-pull first - appointment_status was fetched on page load and may be stale by the time they
    // click (e.g. the team member rescheduled/cancelled it from the internal app since then).
    await this.GetAppointmentStatus();

    const modal = await this.modalController.create({
      component: CreatePage,
      cssClass: 'tall-modal',
      backdropDismiss: false,
      componentProps: {
        existing_booking: this.appointment_status.booking,
      },
    });
    modal.onDidDismiss().then((data: any) => {
      if (data?.data?.refresh) this.GetAppointmentStatus();
    });
    await modal.present();
  }

  async logoutConfirm() {
      const alert = await this.alertController.create({
      header: 'Confirm Logout',
      message: 'Are you sure you want to logout?',
      buttons: [
        {
        text: 'Cancel',
        role: 'cancel'
        },
        {
        text: 'Logout',
        handler: () => {
          // Delete session cookies
          document.cookie.split(";").forEach((c) => {
          document.cookie = c
            .replace(/^ +/, "")
            .replace(/=.*/, "=;expires=" + new Date(0).toUTCString() + ";path=/");
          });
          // Optionally, redirect to login or home page
          window.location.href = '/login';
        }
        }
      ]
      });
      await alert.present();
    }

}
